/* project-planning-state.jsx — shared planning/tickets DRAFT for Project Detail.
   ─────────────────────────────────────────────────────────────────────────────
   WHY THIS EXISTS
   In the original app the Planning & Execution tab kept the editable ticket draft
   *inside* ProjectPlanningExec and saved it with the "Save planning" bar. Now
   that the tickets live in a separate Board tab, BOTH tabs must edit the same draft
   and save together. So the draft is lifted here into a hook that you call ONCE
   in ProjectDetailView and pass to both tabs.

   USAGE (project-detail.jsx):
     const planning = useProjectPlanning(project, onAction);
     ...
     {planning.dirty && (tab === 'planning' || tab === 'board') && <PlanningSaveBar planning={planning} />}
     {tab === 'planning' && <ProjectPlanningExec planning={planning} project={project} db={db}
                              onDrawer={onDrawer} onAction={onAction} DocumentsPanel={DocumentsPanel} Inline={Inline} />}
     {tab === 'board'    && <ProjectBoardTab planning={planning} project={project}
                              statuses={window.AC.PROJECT_TICKET_STATUSES} />}

   Planning-section and ticket persistence is unchanged: onAction('updateProjectPlanning',
   { projectId, planningNotes, workPackages }). Scope concepts and milestones
   persist immediately through their own onAction/onDrawer calls.

   Exports: window.useProjectPlanning, window.PlanningSaveBar
   ───────────────────────────────────────────────────────────────────────────── */
(function () {
  const { useState, useMemo, useEffect } = React;

  const gid = () => (crypto.randomUUID ? crypto.randomUUID()
    : '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
      (Number(c) ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> Number(c) / 4).toString(16)));
  const isGuid = (v) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(v || ''));

  /* ── draft <-> server (de)serialization (identical to the original) ── */
  function sectionTicketPlan(section) {
    const clusters = Array.isArray(section.ticketClusters) ? section.ticketClusters : [];
    return {
      kind: 'clustered-tickets', version: 1,
      categories: clusters.map((cluster) => ({
        id: isGuid(cluster.id) ? cluster.id : gid(),
        name: cluster.name || '',
        items: (cluster.tickets || []).map((ticket) => ({
          id: isGuid(ticket.id) ? ticket.id : gid(),
          number: Number(ticket.number) || 0,
          title: ticket.title || '',
          category: window.AC.cleanProjectTicketCategory(ticket.category),
          done: !!ticket.done,
          status: ticket.status || (ticket.done ? 'Done' : 'Open'),
          remarks: ticket.remarks || '',
          after: ticket.after || '',
          milestoneId: ticket.milestoneId || '',
          requirementId: ticket.requirementId || '',
          gitCommitLinks: Array.isArray(ticket.gitCommitLinks) ? ticket.gitCommitLinks : [],
          messages: Array.isArray(ticket.messages) ? ticket.messages : [],
        })),
      })),
    };
  }
  function ticketClustersFromPlan(section, plan) {
    return (plan.categories || []).map((category, index) => {
      const clusterId = isGuid(category.id) ? category.id : gid();
      return {
        id: clusterId, workPackageId: section.id, position: index, name: category.name || '',
        tickets: (category.items || []).map((item, itemIndex) => ({
          id: isGuid(item.id) ? item.id : gid(),
          number: Number(item.number) || 0,
          ticketClusterId: clusterId, position: itemIndex,
          title: item.title || '', done: !!item.done,
          category: window.AC.cleanProjectTicketCategory(item.category),
          status: item.status || (item.done ? 'Done' : 'Open'),
          remarks: item.remarks || '', after: item.after || '',
          milestoneId: isGuid(item.milestoneId) ? item.milestoneId : null,
          requirementId: isGuid(item.requirementId) ? item.requirementId : null,
          gitCommitLinks: Array.isArray(item.gitCommitLinks) ? item.gitCommitLinks : [],
          messages: Array.isArray(item.messages) ? item.messages : [],
        })),
      };
    });
  }
  function projectPlanningDraft(project) {
    return window.AC.normalizeProjectWorkPackages(project.workPackages, project.planningNotes, project)
      .map((section) => ({ ...section, ticketPlan: sectionTicketPlan(section) }));
  }
  function serializeSections(sections) {
    return sections.map((section, index) => {
      return {
        id: section.id, position: index, name: section.name, phase: section.phase,
        createTests: !!section.createTests,
        planningNotes: section.planningNotes || '',
        ticketClusters: ticketClustersFromPlan(section, section.ticketPlan),
      };
    });
  }
  function nextTicketNumber(sections) {
    const used = new Set();
    (sections || []).forEach((section) => {
      (((section.ticketPlan || {}).categories) || []).forEach((category) => {
        (category.items || []).forEach((item) => {
          const number = Number(item.number) || 0;
          if (number > 0) used.add(number);
        });
      });
    });
    let next = 1;
    while (used.has(next)) next++;
    return next;
  }

  /* ── the hook ── */
  function useProjectPlanning(project, onAction) {
    const [draft, setDraft] = useState(() => projectPlanningDraft(project));
    const [activeId, setActiveId] = useState(() => { const d = projectPlanningDraft(project); return d[0] ? d[0].id : ''; });
    const [saving, setSaving] = useState(false);

    const saved = useMemo(() => projectPlanningDraft(project),
      [project.id, project.planningNotes, project.workPackages]);
    useEffect(() => {
      const next = projectPlanningDraft(project);
      setDraft(next);
      setActiveId((cur) => next.some((s) => s.id === cur) ? cur : (next[0] && next[0].id) || '');
    }, [project.id, project.planningNotes, project.workPackages]);

    const active = draft.find((s) => s.id === activeId) || draft[0];
    const dirty = useMemo(() => JSON.stringify(serializeSections(draft)) !== JSON.stringify(serializeSections(saved)), [draft, saved]);

    const updateActivePlan = (sections, mutate) => sections.map((section) => section.id === active.id
      ? { ...section, ticketPlan: { ...section.ticketPlan, categories: mutate(section.ticketPlan.categories) } }
      : section);
    const patchSection = (id, patch) => setDraft((ss) => ss.map((s) => s.id === id ? { ...s, ...patch } : s));
    const patchActivePlan = (mutate) => setDraft((sections) => updateActivePlan(sections, mutate));

    const renameCat = (catId, name) => patchActivePlan((cats) => cats.map((c) => c.id === catId ? { ...c, name } : c));
    // addTicket RETURNS the new id so the Board can open the edit drawer on the fresh card.
    const addTicket = (catId) => {
      const id = gid();
      const number = nextTicketNumber(draft);
      patchActivePlan((cats) => cats.map((c) => c.id === catId
        ? { ...c, items: [...c.items, { id, number, title: '', category: 'BacklogItem', done: false, status: 'Open', remarks: '', after: '', milestoneId: '', requirementId: '', gitCommitLinks: [], messages: [], _isNew: true }] }
        : c));
      return id;
    };
    const patchTicketIn = (sections, catId, ticketId, patch) => updateActivePlan(sections, (cats) =>
      cats.map((c) => c.id !== catId ? c : { ...c, items: c.items.map((it) => it.id === ticketId ? { ...it, ...patch } : it) }));
    const patchTicket = (catId, ticketId, patch) => setDraft((sections) => patchTicketIn(sections, catId, ticketId, patch));
    const removeTicket = (catId, ticketId) => patchActivePlan((cats) => cats.map((c) => c.id !== catId ? c : { ...c, items: c.items.filter((it) => it.id !== ticketId) }));
    const removeCluster = (catId) => patchActivePlan((cats) => {
      const cluster = cats.find((c) => c.id === catId);
      if (!cluster) return cats;
      if (cluster.items.length) return cats;
      return cats.filter((c) => c.id !== catId);
    });
    // List view move — keeps status, changes cluster only.
    const moveTicket = (fromCatId, ticketId, toCatId) => patchActivePlan((cats) => {
      if (fromCatId === toCatId) return cats;
      const source = cats.find((c) => c.id === fromCatId);
      const ticket = source && source.items.find((item) => item.id === ticketId);
      if (!ticket) return cats;
      return cats.map((c) => {
        if (c.id === fromCatId) return { ...c, items: c.items.filter((item) => item.id !== ticketId) };
        if (c.id === toCatId) return { ...c, items: [...c.items, ticket] };
        return c;
      });
    });
    // Board/drop drawer move — changes status (column), cluster (lane), and optional edited fields in one move.
    const moveTicketStatusIn = (sections, fromCatId, ticketId, toCatId, status, patch) => updateActivePlan(sections, (cats) => {
      const source = cats.find((c) => c.id === fromCatId);
      const ticket = source && source.items.find((item) => item.id === ticketId);
      if (!ticket) return cats;
      const moved = { ...ticket, ...(patch || {}), status, done: status === 'Done' };
      if (fromCatId === toCatId) return cats.map((c) => c.id !== toCatId ? c : { ...c, items: c.items.map((i) => i.id === ticketId ? moved : i) });
      return cats.map((c) => {
        if (c.id === fromCatId) return { ...c, items: c.items.filter((i) => i.id !== ticketId) };
        if (c.id === toCatId) return { ...c, items: [...c.items, moved] };
        return c;
      });
    });
    const moveTicketStatus = (fromCatId, ticketId, toCatId, status, patch) => setDraft((sections) => moveTicketStatusIn(sections, fromCatId, ticketId, toCatId, status, patch));
    const addCluster = () => {
      patchActivePlan((cats) => [{ id: gid(), name: '', items: [] }, ...cats]);
    };
    const addWorkPackage = () => {
      const next = window.AC.newProjectWorkPackage(`New ${window.AC.projectItemLabel(project).toLowerCase()}`, project);
      const withPlan = { ...next, position: draft.length, ticketPlan: sectionTicketPlan(next) };
      setDraft((d) => [...d, withPlan]);
      setActiveId(withPlan.id);
    };
    async function saveSections(sections) {
      setSaving(true);
      try {
        const serialized = serializeSections(sections);
        await onAction('updateProjectPlanning', {
          projectId: project.id,
          planningNotes: serialized[0] ? serialized[0].planningNotes : '',
          workPackages: serialized,
        });
      } finally { setSaving(false); }
    }
    async function updateWorkPackagePlanningNotes(workPackageId, planningNotes) {
      const next = draft.map((section) => section.id === workPackageId ? { ...section, planningNotes: planningNotes || '' } : section);
      setDraft(next);
      await saveSections(next);
    }
    async function save() {
      await saveSections(draft);
    }
    async function patchTicketAndSave(catId, ticketId, patch) {
      const next = patchTicketIn(draft, catId, ticketId, patch);
      setDraft(next);
      await saveSections(next);
    }
    async function moveTicketStatusAndSave(fromCatId, ticketId, toCatId, status, patch) {
      const next = moveTicketStatusIn(draft, fromCatId, ticketId, toCatId, status, patch);
      setDraft(next);
      await saveSections(next);
    }
    const discard = () => setDraft(projectPlanningDraft(project));

    return {
      draft, active, activeId, setActiveId, dirty, saving, save, discard,
      patchSection, renameCat, addCluster, removeCluster,
      updateWorkPackagePlanningNotes,
      addTicket, patchTicket, patchTicketAndSave, removeTicket, moveTicket, moveTicketStatus, moveTicketStatusAndSave, addWorkPackage,
      get cats() { return active && active.ticketPlan ? active.ticketPlan.categories : []; },
    };
  }

  /* shared save bar — render once in the detail shell, visible on both tabs */
  function PlanningSaveBar({ planning }) {
    if (!planning.dirty) return null;
    return (
      <div className="pd-savebar">
        <Icon name="alert" size={13} /><span style={{ flex: 1 }}>Unsaved planning changes</span>
        <button className="btn sm ghost" onClick={planning.discard} disabled={planning.saving}>Discard</button>
        <button className="btn sm" onClick={planning.save} disabled={planning.saving}><Icon name="check" size={13} />{planning.saving ? 'Saving…' : 'Save planning'}</button>
      </div>
    );
  }

  Object.assign(window, { useProjectPlanning, PlanningSaveBar });
})();
